import { useEffect, useState } from 'react';
import { Inbox } from 'lucide-react';
import { formatDate, formatTime } from '@/shared/lib/format';
import { Badge } from '@/shared/ui/badge';
import { Pagination } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { EmptyState } from '@/shared/components/empty-state';
import { useAiModels, useUsage, type UsageFilter } from '../api/use-ai-admin';
import { formatTokens, formatUsd, modelLabel } from '../model/format';

const PAGE_SIZE = 20;

interface UsageListProps {
  filter: UsageFilter;
  enabled?: boolean;
  /** Esconde "usuário · passbook" quando o filtro já é de um usuário. */
  hideUser?: boolean;
}

/** Extrato por conversa: custo real, tokens e a segmentação que saiu dela. */
export function UsageList({ filter, enabled = true, hideUser }: UsageListProps) {
  const [page, setPage] = useState(0);
  const { data: catalog } = useAiModels();
  const { data, isLoading } = useUsage(filter, page, enabled);
  const filterKey = JSON.stringify(filter);

  useEffect(() => setPage(0), [filterKey]);

  if (isLoading) {
    return (
      <div className="grid gap-2 md:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!data || data.rows.length === 0) {
    return <EmptyState icon={Inbox} title="Sem uso registrado" description="Nenhuma conversa com a IA ainda." />;
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-2 md:grid-cols-2">
        {data.rows.map((row) => (
          <article key={row.id} className="rounded-xl border border-border/50 bg-foreground/[0.02] p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {row.segmentation?.name ?? 'Conversa sem segmentação salva'}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {hideUser ? row.passbook.name : `${row.user.name} · ${row.passbook.name}`}
                </p>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">{formatUsd(row.costUsd)}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>
                {formatDate(row.endedAt)} {formatTime(row.endedAt)}
              </span>
              <span>{modelLabel(row.model, catalog?.models)}</span>
              <span className="tabular-nums">
                {formatTokens(row.inputTokens)} entrada · {formatTokens(row.outputTokens)} saída
              </span>
              <span>
                {row.calls} {row.calls === 1 ? 'chamada' : 'chamadas'}
              </span>
              {row.aborted ? <Badge variant="warning">Interrompida</Badge> : null}
            </div>
          </article>
        ))}
      </div>
      {data.total > PAGE_SIZE ? (
        <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} />
      ) : null}
    </div>
  );
}
